import { useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Checkbox } from "../../../ui/checkbox";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../../ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../../ui/select";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../../ui/dialog";
import { Label } from "../../../ui/label";
import {
    AlertTriangle,
    Search,
    Radar,
    Server,
    Monitor,
    Plus,
    RefreshCw,
    Terminal,
    CheckCircle2,
    AlertCircle,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    Loader2
} from "lucide-react";
import { Badge } from "../../../ui/badge";
import { useToast } from "../../../../hooks/use-toast";
import AddManualDevice from "../AddManualDevice";
import { Device } from "../../interfaces/EndpointInterfaces";
import {
    getNetworkScanResult,
    getNetworkScanStatus,
    NetworkScanResultResponse,
    NetworkScanStatusResponse,
    performNetworkScan,
} from "../Api/EndpointApi";
import { myLocalStorage } from "../../../StorageHelper";
import { formatTimeAgo } from "../../formatTimeAgo";

interface DiscoveredDevicesTabProps {
    device: Device;
}

interface DiscoveredDevice {
    id: string;
    hostname: string;
    ipAddress: string;
    port: number;
    protocol: "SSH" | "RDP";
    subnet: string;
    discoveredAt: string;
    status: "new" | "onboarded";
    operatingSystem?: string;
}

const getProtocolForPort = (port: number): DiscoveredDevice["protocol"] | null => {
    if (port === 22) {
        return "SSH";
    }

    if (port === 3389) {
        return "RDP";
    }

    return null;
};

const formatDateTime = (value?: string) => {
    if (!value) {
        return "Not available";
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
        return value;
    }

    return parsedDate.toLocaleString();
};

const formatDate = (value?: string) => {
    if (!value) {
        return "-";
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
        return "-";
    }

    return parsedDate.toLocaleDateString();
};

const getDefaultOperatingSystem = (protocol: DiscoveredDevice["protocol"]) => {
    return protocol === "SSH" ? "Linux" : "Windows";
};

const mapScanResponseToDevices = (scanResponse: NetworkScanResultResponse | null): DiscoveredDevice[] => {
    if (!scanResponse?.results?.length) {
        return [];
    }

    return scanResponse.results.flatMap((result, resultIndex) => {
        return (result.openPorts ?? []).flatMap((openPort, portIndex) => {
            const normalizedPort = Number(openPort.port);
            const protocol = getProtocolForPort(normalizedPort);

            if (!protocol || !openPort.ipAddress) {
                return [];
            }

            const hostname = openPort.hostname?.trim() || openPort.ipAddress;

            return [{
                id: `${result.cidr}-${openPort.ipAddress}-${normalizedPort}-${resultIndex}-${portIndex}`,
                hostname,
                ipAddress: openPort.ipAddress,
                port: normalizedPort,
                protocol,
                subnet: result.cidr,
                discoveredAt: scanResponse.created_at || "",
                status: openPort.onboarded ? "onboarded" : "new",
                operatingSystem: openPort.operatingSystem?.trim() || getDefaultOperatingSystem(protocol),
            }];
        });
    });
};

const extractScanLastTime = (response: NetworkScanStatusResponse | null): string =>
    response?.lastScanTime ?? response?.last_scan_time ?? "";

export default function DiscoveredDevicesTab({ device }: DiscoveredDevicesTabProps) {
    const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedSubnet, setSelectedSubnet] = useState<string>("all");
    const [selectedPort, setSelectedPort] = useState<string>("all");
    const [selectedStatus, setSelectedStatus] = useState<string>("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [isOnboardDialogOpen, setIsOnboardDialogOpen] = useState(false);
    const [deviceToOnboard, setDeviceToOnboard] = useState<DiscoveredDevice | null>(null);
    const [discoveredDevices, setDiscoveredDevices] = useState<DiscoveredDevice[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isScanning, setIsScanning] = useState(false);
    const [isScanStatusActive, setIsScanStatusActive] = useState(false);
    const [lastScanTime, setLastScanTime] = useState<string>("");
    const [scanStatusLastTime, setScanStatusLastTime] = useState<string>("");
    const [fetchError, setFetchError] = useState("");
    const [isRescanDialogOpen, setIsRescanDialogOpen] = useState(false);
    const [scanTimeout, setScanTimeout] = useState("300");
    const [selectedScanSubnets, setSelectedScanSubnets] = useState<Set<string>>(new Set());

    const { toast } = useToast();
    const itemsPerPage = 15;
    const deferredSearchTerm = useDeferredValue(searchTerm);
    const isScanActive = isScanning || isScanStatusActive;
    const parsedScanTimeout = Number.parseInt(scanTimeout, 10);
    const isScanTimeoutValid =
        Number.isFinite(parsedScanTimeout) &&
        parsedScanTimeout >= 200 &&
        parsedScanTimeout <= 2000;

    const loadScanStatus = useCallback(async (signal?: AbortSignal) => {
        if (!tenantName || !device?.id) {
            setIsScanStatusActive(false);
            return;
        }

        try {
            const response = await getNetworkScanStatus(tenantName, device.id, signal);
            const active = response?.status === true;
            setIsScanStatusActive(active);
            const time = extractScanLastTime(response);
            if (time) {
                setScanStatusLastTime(time);
            }
        } catch (error: any) {
            if (!signal?.aborted) {
                setIsScanStatusActive(false);
            }
        }
    }, [device?.id, tenantName]);

    const loadDiscoveredDevices = useCallback(async (options: { silent?: boolean; signal?: AbortSignal } = {}) => {
        const { silent = false, signal } = options;

        if (!tenantName || !device?.id) {
            setDiscoveredDevices([]);
            setLastScanTime("");
            setFetchError("Missing tenant or device identifier.");
            setIsLoading(false);
            return;
        }

        if (!silent) {
            setIsLoading(true);
        }

        setFetchError("");

        try {
            const response = await getNetworkScanResult(tenantName, device.id, signal);

            if (!response || response.status !== "success") {
                throw new Error("No data found.");
            }

            setDiscoveredDevices(mapScanResponseToDevices(response));
            setLastScanTime(response.created_at || "");
        } catch (error: any) {
            if (signal?.aborted) {
                return;
            }

            const message = error?.message || "No data found.";
            if (!silent) {
                setDiscoveredDevices([]);
            }
            setFetchError(message);
            console.error("Failed to fetch discovered devices:", error);
        } finally {
            if (!signal?.aborted) {
                setIsLoading(false);
            }
        }
    }, [device?.id, tenantName]);

    useEffect(() => {
        const controller = new AbortController();
        void loadDiscoveredDevices({ signal: controller.signal });
        void loadScanStatus(controller.signal);

        return () => {
            controller.abort();
        };
    }, [loadDiscoveredDevices, loadScanStatus]);

    useEffect(() => {
        if (!device?.id || !tenantName) {
            return;
        }

        const controller = new AbortController();
        const intervalId = window.setInterval(() => {
            void loadScanStatus(controller.signal);
        }, 10000);

        return () => {
            controller.abort();
            window.clearInterval(intervalId);
        };
    }, [device?.id, loadScanStatus, tenantName]);

    // Filter devices
    const filteredDevices = useMemo(() => {
        return discoveredDevices.filter(d => {
            const matchesSearch =
                d.hostname.toLowerCase().includes(deferredSearchTerm.toLowerCase()) ||
                d.ipAddress.includes(deferredSearchTerm) ||
                d.port.toString().includes(deferredSearchTerm) ||
                d.operatingSystem?.toLowerCase().includes(deferredSearchTerm.toLowerCase()) ||
                d.status.toLowerCase().includes(deferredSearchTerm.toLowerCase());

            const matchesSubnet = selectedSubnet === "all" || d.subnet === selectedSubnet;
            const matchesPort = selectedPort === "all" ||
                (selectedPort === "22" && d.port === 22) ||
                (selectedPort === "3389" && d.port === 3389);
            const matchesStatus = selectedStatus === "all" || d.status === selectedStatus;

            return matchesSearch && matchesSubnet && matchesPort && matchesStatus;
        });
    }, [deferredSearchTerm, discoveredDevices, selectedSubnet, selectedPort, selectedStatus]);

    // Pagination
    const totalPages = Math.ceil(filteredDevices.length / itemsPerPage);
    const paginatedDevices = filteredDevices.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Reset page when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [deferredSearchTerm, selectedSubnet, selectedPort, selectedStatus]);

    // Stats
    const stats = useMemo(() => {
        const byStatus = {
            new: discoveredDevices.filter(d => d.status === "new").length,
            onboarded: discoveredDevices.filter(d => d.status === "onboarded").length
        };
        const byProtocol = {
            ssh: discoveredDevices.filter(d => d.protocol === "SSH").length,
            rdp: discoveredDevices.filter(d => d.protocol === "RDP").length
        };
        return { byStatus, byProtocol, total: discoveredDevices.length };
    }, [discoveredDevices]);

    const configuredSubnets = useMemo(() => {
        const rawSubnets = Array.isArray(device?.gatewayIpAddresses) ? device.gatewayIpAddresses : [];
        return Array.from(new Set(rawSubnets.map((subnet) => subnet?.trim()).filter((subnet): subnet is string => Boolean(subnet))));
    }, [device?.gatewayIpAddresses]);

    const handleOpenRescanDialog = () => {
        setSelectedScanSubnets(new Set(configuredSubnets));
        setIsRescanDialogOpen(true);
    };

    const handleToggleScanSubnet = (subnet: string, checked: boolean) => {
        const nextSet = new Set(Array.from(selectedScanSubnets));
        if (checked) {
            nextSet.add(subnet);
        } else {
            nextSet.delete(subnet);
        }
        setSelectedScanSubnets(nextSet);
    };

    const handleStartScan = async () => {
        if (!device?.id) {
            return;
        }

        const subnetsToScan = Array.from(selectedScanSubnets);
        if (subnetsToScan.length === 0) {
            toast({
                title: "No data found",
                description: "No subnet selected.",
                variant: "destructive"
            });
            return;
        }

        setIsScanning(true);

        try {
            const parsedTimeout = Number.parseInt(scanTimeout, 10);
            await performNetworkScan(
                device.id,
                subnetsToScan,
                Number.isNaN(parsedTimeout) ? undefined : parsedTimeout,
            );
            setIsRescanDialogOpen(false);
            toast({
                title: "Scan Started",
                description: `Discovery scan started for ${device.name} across ${subnetsToScan.length} subnet(s).`,
            });
            setIsScanStatusActive(true);
            await loadDiscoveredDevices({ silent: true });
            await loadScanStatus();
        } catch (error: any) {
            const message = error?.message || "Unable to start discovery scan.";
            toast({
                title: "Scan Failed",
                description: message,
                variant: "destructive"
            });
        } finally {
            setIsScanning(false);
        }
    };

    const handleOnboardDevice = (discoveredDevice: DiscoveredDevice) => {
        setDeviceToOnboard(discoveredDevice);
        setIsOnboardDialogOpen(true);
    };

    const handleOnboardDialogClose = (open: boolean) => {
        if (!open) {
            setDeviceToOnboard(null);
            setIsOnboardDialogOpen(false);
            void loadDiscoveredDevices({ silent: true });
        }
    };

    const getStatusBadge = (status: DiscoveredDevice["status"]) => {
        switch (status) {
            case "new":
                return (
                    <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300 gap-1">
                        <AlertCircle className="w-3 h-3" />
                        New
                    </Badge>
                );
            case "onboarded":
                return (
                    <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300 gap-1">
                        <CheckCircle2 className="w-3 h-3" />
                        Onboarded
                    </Badge>
                );
        }
    };

    const getProtocolBadge = (protocol: "SSH" | "RDP", port: number) => {
        if (protocol === "SSH") {
            return (
                <Badge variant="outline" className="font-mono text-xs bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800 gap-1">
                    <Terminal className="w-3 h-3" />
                    SSH:{port}
                </Badge>
            );
        }
        return (
            <Badge variant="outline" className="font-mono text-xs bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800 gap-1">
                <Monitor className="w-3 h-3" />
                RDP:{port}
            </Badge>
        );
    };

    const uniqueSubnets = useMemo(() => {
        return Array.from(new Set(discoveredDevices.map(d => d.subnet)));
    }, [discoveredDevices]);

    return (
        <div className="space-y-4 p-6" data-testid="discovered-devices-tab">
            {/* Compact Header with Stats */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <div>
                        <h2 className="text-xl font-semibold text-foreground flex items-center gap-2" data-testid="title-discovered-devices">
                            <Radar className="w-5 h-5 text-blue-600" />
                            Discovered Devices
                        </h2>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            Network discovery for SSH/RDP endpoints •{" "}
                            {isScanActive
                                ? <span className="text-blue-500 font-medium">Scan in progress...</span>
                                : <>Last scan: {formatTimeAgo(scanStatusLastTime || lastScanTime)}</>}
                        </p>
                    </div>
                </div>

                {/* Quick Stats */}
                <div className="flex items-center gap-3 text-xs">
                    <div className="flex items-center gap-4 px-3 py-1.5 bg-muted/50 rounded-md">
                        <span className="text-muted-foreground">Total: <span className="font-semibold text-foreground">{stats.total}</span></span>
                        <span className="text-blue-600">New: <span className="font-semibold">{stats.byStatus.new}</span></span>
                        <span className="text-green-600">Onboarded: <span className="font-semibold">{stats.byStatus.onboarded}</span></span>
                        <span className="text-muted-foreground">SSH: <span className="font-medium">{stats.byProtocol.ssh}</span></span>
                        <span className="text-muted-foreground">RDP: <span className="font-medium">{stats.byProtocol.rdp}</span></span>
                    </div>

                    <Button
                        size="sm"
                        onClick={handleOpenRescanDialog}
                        disabled={isScanActive}
                        className="gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid="button-rescan"
                    >
                        {isScanActive ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                            <RefreshCw className="w-3.5 h-3.5" />
                        )}
                        {isScanActive ? "Scanning..." : "Rescan"}
                    </Button>
                </div>
            </div>

            {/* Filters Row */}
            <Card className="border-muted">
                <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                        <div className="relative flex-1 max-w-sm">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Search hostname, IP, or port..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9 h-9"
                                data-testid="input-search-discovered"
                            />
                        </div>

                        <Select value={selectedSubnet} onValueChange={setSelectedSubnet}>
                            <SelectTrigger className="w-48 h-9" data-testid="select-subnet-filter">
                                <SelectValue placeholder="All Subnets" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Subnets</SelectItem>
                                {uniqueSubnets.map(subnet => (
                                    <SelectItem key={subnet} value={subnet}>
                                        {subnet}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Select value={selectedPort} onValueChange={setSelectedPort}>
                            <SelectTrigger className="w-32 h-9" data-testid="select-port-filter">
                                <SelectValue placeholder="All Ports" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Ports</SelectItem>
                                <SelectItem value="22">SSH (22)</SelectItem>
                                <SelectItem value="3389">RDP (3389)</SelectItem>
                            </SelectContent>
                        </Select>

                        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                            <SelectTrigger className="w-36 h-9" data-testid="select-status-filter">
                                <SelectValue placeholder="All Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Status</SelectItem>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="onboarded">Onboarded</SelectItem>
                            </SelectContent>
                        </Select>

                        <div className="text-xs text-muted-foreground ml-auto">
                            {filteredDevices.length} device{filteredDevices.length !== 1 ? 's' : ''} found
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Devices Table */}
            <Card>
                <CardContent className="p-0">
                    {isLoading ? (
                        <div className="flex items-center justify-center gap-2 p-12 text-sm text-muted-foreground">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading discovered devices...
                        </div>
                    ) : fetchError && discoveredDevices.length === 0 ? (
                        <div className="p-12 text-center space-y-4">
                            <div>
                                <p className="text-muted-foreground text-sm">No device found.</p>
                            </div>

                        </div>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/50">
                                            <TableHead className="font-semibold pl-4">IP Address</TableHead>
                                            <TableHead className="font-semibold">Protocol</TableHead>
                                            <TableHead className="font-semibold">Hostname</TableHead>
                                            <TableHead className="font-semibold">Subnet</TableHead>
                                            <TableHead className="font-semibold">OS</TableHead>
                                            <TableHead className="font-semibold">Status</TableHead>
                                            <TableHead className="font-semibold">Discovered</TableHead>
                                            <TableHead className="pr-4 font-semibold">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {paginatedDevices.map((discoveredDevice) => (
                                            <TableRow
                                                key={discoveredDevice.id}
                                                data-testid={`row-discovered-device-${discoveredDevice.id}`}
                                                className="hover:bg-muted/30"
                                            >
                                                <TableCell className="pl-4">
                                                    <span className="font-mono text-sm" data-testid={`text-ip-${discoveredDevice.id}`}>
                                                        {discoveredDevice.ipAddress}
                                                    </span>
                                                </TableCell>

                                                <TableCell>
                                                    {getProtocolBadge(discoveredDevice.protocol, discoveredDevice.port)}
                                                </TableCell>

                                                <TableCell>
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-7 h-7 bg-primary/10 rounded-md flex items-center justify-center">
                                                            {discoveredDevice.protocol === "SSH" ? (
                                                                <Server className="w-3.5 h-3.5 text-primary" />
                                                            ) : (
                                                                <Monitor className="w-3.5 h-3.5 text-primary" />
                                                            )}
                                                        </div>
                                                        <span
                                                            className="font-medium text-sm max-w-[180px] truncate"
                                                            title={discoveredDevice.hostname || undefined}
                                                            data-testid={`text-hostname-${discoveredDevice.id}`}
                                                        >
                                                            {discoveredDevice.hostname.length > 24
                                                                ? `${discoveredDevice.hostname.slice(0, 24)}...`
                                                                : discoveredDevice.hostname}
                                                        </span>
                                                    </div>
                                                </TableCell>

                                                <TableCell>
                                                    <span className="font-mono text-xs text-muted-foreground">
                                                        {discoveredDevice.subnet}
                                                    </span>
                                                </TableCell>

                                                <TableCell>
                                                    <span className="text-sm text-muted-foreground">
                                                        {discoveredDevice.operatingSystem || "Unknown"}
                                                    </span>
                                                </TableCell>

                                                <TableCell>
                                                    {getStatusBadge(discoveredDevice.status)}
                                                </TableCell>

                                                <TableCell>
                                                    <span className="text-xs text-muted-foreground">
                                                        {formatDate(discoveredDevice.discoveredAt)}
                                                    </span>
                                                </TableCell>

                                                <TableCell className="pr-4">
                                                    {discoveredDevice.status === "new" ? (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-7 text-xs gap-1 text-blue-600 border-blue-200 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-800 dark:hover:bg-blue-950"
                                                            onClick={() => handleOnboardDevice(discoveredDevice)}
                                                            data-testid={`button-onboard-${discoveredDevice.id}`}
                                                        >
                                                            <Plus className="w-3 h-3" />
                                                            Onboard
                                                        </Button>
                                                    ) : (
                                                        <Badge variant="outline" className="text-xs text-green-600 border-green-200">
                                                            JIT Ready
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>

                            {paginatedDevices.length === 0 && (
                                <div className="p-12 text-center">
                                    <Radar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                    <h3 className="text-lg font-medium text-foreground mb-2">No data found</h3>
                                    <p className="text-muted-foreground text-sm mb-4">
                                        No data found.
                                    </p>
                                    {!searchTerm && selectedSubnet === "all" && selectedPort === "all" && selectedStatus === "all" && (
                                        <Button onClick={handleOpenRescanDialog} className="gap-2" disabled={isScanActive}>
                                            {isScanActive ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radar className="w-4 h-4" />}
                                            {isScanActive ? "Scanning..." : "Start Discovery Scan"}
                                        </Button>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                        Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                        {Math.min(currentPage * itemsPerPage, filteredDevices.length)} of{' '}
                        {filteredDevices.length} devices
                    </div>
                    <div className="flex items-center gap-1">
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setCurrentPage(1)}
                            disabled={currentPage === 1}
                            data-testid="button-first-page"
                        >
                            <ChevronsLeft className="w-4 h-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            data-testid="button-previous-page"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </Button>

                        <div className="flex items-center gap-1 mx-2">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                } else {
                                    pageNum = currentPage - 2 + i;
                                }

                                return (
                                    <Button
                                        key={pageNum}
                                        variant={currentPage === pageNum ? "default" : "outline"}
                                        size="icon"
                                        className="h-8 w-8"
                                        onClick={() => setCurrentPage(pageNum)}
                                        data-testid={`button-page-${pageNum}`}
                                    >
                                        {pageNum}
                                    </Button>
                                );
                            })}
                        </div>

                        <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            data-testid="button-next-page"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setCurrentPage(totalPages)}
                            disabled={currentPage === totalPages}
                            data-testid="button-last-page"
                        >
                            <ChevronsRight className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Onboard Device Dialog - reuses AddManualDevice with prefilled values */}
            {deviceToOnboard && (
                <AddManualDevice
                    open={isOnboardDialogOpen}
                    onOpenChange={handleOnboardDialogClose}
                    prefilledDevice={{
                        ipAddress: deviceToOnboard.ipAddress,
                        hostname: deviceToOnboard.hostname,
                        port: deviceToOnboard.port,
                        protocol: deviceToOnboard.protocol,
                        gatewayName: device.name
                    }}
                />
            )}
            {/* Rescan Configuration Dialog */}
            <Dialog open={isRescanDialogOpen} onOpenChange={setIsRescanDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Radar className="w-5 h-5 text-blue-600" />
                            Network Discovery Scan
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-4">
                        {/* Subnet Selection */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Select Subnets to Scan</Label>
                            <div className="border rounded-lg p-3 space-y-2 max-h-40 overflow-auto">
                                {configuredSubnets.length > 0 ? (
                                    configuredSubnets.map((subnet, index) => {
                                        const inputId = `subnet-${index}`;
                                        return (
                                            <div key={subnet} className="flex items-center gap-3">
                                                <Checkbox
                                                    id={inputId}
                                                    checked={selectedScanSubnets.has(subnet)}
                                                    onCheckedChange={(checked) => handleToggleScanSubnet(subnet, !!checked)}
                                                    data-testid={`checkbox-subnet-${index}`}
                                                />
                                                <label
                                                    htmlFor={inputId}
                                                    className="text-sm font-mono cursor-pointer"
                                                >
                                                    {subnet}
                                                </label>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <p className="text-sm text-muted-foreground">No data found.</p>
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {selectedScanSubnets.size} of {configuredSubnets.length} subnet(s) selected
                            </p>
                        </div>

                        {/* Discovery Timeout */}
                        <div className="space-y-2">
                            <Label htmlFor="scan-timeout" className="text-sm font-medium">
                                Per Device Discovery Timeout (ms)
                            </Label>
                            <Input
                                id="scan-timeout"
                                type="number"
                                min="200"
                                max="2000"
                                step="100"
                                value={scanTimeout}
                                onChange={(e) => setScanTimeout(e.target.value)}
                                className="font-mono"
                                data-testid="input-scan-timeout"
                            />
                            <p className="text-xs text-muted-foreground">
                                Socket connect timeout for each port check. Default: 300ms
                            </p>
                        </div>

                        {/* Warning for high timeout */}
                        {parseInt(scanTimeout) > 300 && (
                            <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                                <AlertTriangle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                                <div className="text-xs text-yellow-700 dark:text-yellow-300">
                                    <p className="font-medium">Increased scan duration</p>
                                    <p className="mt-0.5">
                                        Higher timeout values will increase the total scan duration, especially for large subnets.
                                        Only increase if network latency causes false negatives.
                                    </p>
                                </div>
                            </div>
                        )}

                        {parseInt(scanTimeout) < 200 && (
                            <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                                <div className="text-xs text-amber-700 dark:text-amber-300">
                                    <p className="font-medium">Decreased scan duration</p>
                                    <p className="mt-0.5">
                                        Lower timeout values can miss open ports if the latency to devices is high.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setIsRescanDialogOpen(false)}
                            data-testid="button-cancel-rescan"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleStartScan}
                            disabled={selectedScanSubnets.size === 0 || isScanActive || !isScanTimeoutValid}
                            className="gap-2"
                            data-testid="button-start-scan"
                        >
                            <Radar className="w-4 h-4" />
                            Start Scan
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}